import { useEffect, useMemo, useState } from "react";
import { Plus, PencilSimple, Trash, CheckCircle, XCircle, Function } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = {
  code: "", name: "", category: "", description: "", formula: "", unit: "",
  comparison: "gte", green_threshold: 0, yellow_threshold: 0, decimals: 1, active: true,
};

export default function KPIs() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [kpis, setKpis] = useState([]);
  const [variables, setVariables] = useState([]);
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [validation, setValidation] = useState(null);

  const load = () => {
    api.get("/kpis").then((res) => setKpis(res.data));
  };
  useEffect(() => {
    load();
    api.get("/variables").then((res) => setVariables(res.data));
  }, []);

  const categories = useMemo(() => Array.from(new Set(kpis.map((k) => k.category).filter(Boolean))), [kpis]);
  const filtered = kpis.filter((k) => category === "all" || k.category === category);

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setValidation(null);
    setOpen(true);
  };
  const openEdit = (k) => {
    setForm({ ...k });
    setEditing(k.id);
    setValidation(null);
    setOpen(true);
  };

  const insertVar = (code) => {
    setForm((f) => ({ ...f, formula: `${f.formula}${f.formula ? " " : ""}${code}` }));
  };

  const validate = async () => {
    try {
      const { data } = await api.post("/kpis/validate-formula", { formula: form.formula });
      setValidation(data);
      if (data.error) toast.error(data.error);
      else if (data.valid) toast.success("Fórmula válida");
      else toast.error(`Variables inexistentes: ${data.missing.join(", ")}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    const payload = {
      ...form,
      green_threshold: parseFloat(form.green_threshold),
      yellow_threshold: parseFloat(form.yellow_threshold),
      decimals: parseInt(form.decimals, 10),
    };
    try {
      if (editing) await api.put(`/kpis/${editing}`, payload);
      else await api.post("/kpis", payload);
      toast.success(editing ? "KPI actualizado" : "KPI creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`¿Eliminar el KPI "${k.name}"?`)) return;
    await api.delete(`/kpis/${k.id}`);
    toast.success("KPI eliminado");
    load();
  };

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Indicadores (KPI)</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {kpis.length} indicadores · motor de fórmulas desacoplado del origen de datos
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="kpi-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={openNew} data-testid="new-kpi-button">
              <Plus size={18} className="mr-1" aria-hidden="true" /> Nuevo KPI
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm" data-testid="kpis-table">
          <thead className="bg-accent/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Indicador</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Fórmula</th>
              <th className="px-4 py-3">Meta</th>
              <th className="px-4 py-3">Unidad</th>
              {isAdmin && <th className="px-4 py-3 text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((k) => (
              <tr key={k.id} className="border-t border-border hover:bg-accent/30" data-testid={`kpi-row-${k.code}`}>
                <td className="px-4 py-2.5 font-medium">{k.name}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{k.category}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{k.formula}</td>
                <td className="px-4 py-2.5 text-muted-foreground">
                  {k.comparison === "gte" ? "≥" : "≤"} {k.green_threshold}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{k.unit}</td>
                {isAdmin && (
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(k)} data-testid={`edit-kpi-${k.code}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(k)} data-testid={`delete-kpi-${k.code}`}>
                        <Trash size={16} className="text-danger" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="kpi-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar KPI" : "Nuevo KPI"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Código</Label>
                <Input
                  value={form.code}
                  disabled={!!editing}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="margen_por_empleado"
                  data-testid="kpi-code-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kpi-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kpi-category-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Unidad</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="%, MXN/empleado…" />
              </div>
            </div>

            {/* Formula builder */}
            <div className="space-y-2 rounded-lg border border-border bg-zinc-950 p-3">
              <Label className="text-zinc-400">Fórmula (expresión matemática)</Label>
              <Textarea
                value={form.formula}
                onChange={(e) => setForm({ ...form, formula: e.target.value })}
                className="border-zinc-800 bg-black font-mono text-sm text-emerald-400 placeholder:text-zinc-600"
                placeholder="(variable_a / variable_b) * 100"
                rows={2}
                data-testid="formula-input-field"
              />
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button type="button" size="sm" variant="secondary" onClick={validate} data-testid="validate-formula-button">
                  <Function size={15} className="mr-1" aria-hidden="true" /> Validar
                </Button>
                {validation && (
                  <span className={`flex items-center gap-1 text-xs ${validation.valid ? "text-emerald-400" : "text-red-400"}`}>
                    {validation.valid ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    {validation.valid ? "Válida" : (validation.error || `Faltan: ${validation.missing?.join(", ")}`)}
                  </span>
                )}
              </div>
              <div className="max-h-28 overflow-y-auto pt-1">
                <p className="mb-1 text-xs text-zinc-500">Insertar variable:</p>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => insertVar(v.code)}
                      className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 font-mono text-[11px] text-zinc-300 hover:border-emerald-500 hover:text-emerald-400"
                    >
                      {v.code}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Dirección de la meta</Label>
                <Select value={form.comparison} onValueChange={(v) => setForm({ ...form, comparison: v })}>
                  <SelectTrigger data-testid="kpi-comparison-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gte">Mayor es mejor (≥)</SelectItem>
                    <SelectItem value="lte">Menor es mejor (≤)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Decimales</Label>
                <Input type="number" value={form.decimals} onChange={(e) => setForm({ ...form, decimals: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Umbral verde (en meta)</Label>
                <Input type="number" value={form.green_threshold} onChange={(e) => setForm({ ...form, green_threshold: e.target.value })} data-testid="kpi-green-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Umbral amarillo (riesgo)</Label>
                <Input type="number" value={form.yellow_threshold} onChange={(e) => setForm({ ...form, yellow_threshold: e.target.value })} data-testid="kpi-yellow-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-kpi-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
